import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import * as Sentry from '@sentry/react';
import Icon from '@components/ui/Icon';
import Button from '@components/ui/Button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Error boundary wrapping the 3D Canvas. If Three.js or r3f throws during
 * render, this shows a friendly fallback instead of a blank viewport.
 */
interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message: string;
}

export default class CanvasErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, message: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, message: error.message };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[CanvasErrorBoundary]', error, info.componentStack);
    Sentry.captureException(error, {
      tags: { boundary: 'canvas' },
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, message: '' });
  };

  override render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'grid',
            placeItems: 'center',
            background: 'var(--bg-base)',
            color: 'var(--text-muted)',
            padding: 'var(--space-3xl)',
          }}
        >
          <div
            style={{
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--space-md)',
              maxWidth: 360,
            }}
          >
            <Icon icon={AlertTriangle} size="lg" color="var(--accent-amber)" />
            <div
              style={{
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                fontSize: 14,
                color: 'var(--text-primary)',
              }}
            >
              3D viewport encountered an error
            </div>
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--text-hint)',
                lineHeight: 1.5,
                wordBreak: 'break-word',
              }}
            >
              {this.state.message || 'Unknown error'}
            </div>
            <Button
              variant="secondary"
              size="sm"
              icon={<Icon icon={RefreshCw} size={14} />}
              onClick={this.handleRetry}
            >
              Retry
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
